/* ============================================================
   custom-select.js — dark, on-brand dropdowns
   ------------------------------------------------------------
   Progressive enhancement for any <select data-custom-select>.
   The native <select> stays in the DOM as the value-holder (form
   submit + change events fire exactly as before), so existing
   listeners — e.g. the specialist finder's region/language
   matching — keep working untouched. We only replace the visual:
   a styled trigger + a grey/black listbox the OS can't darken.

   Shared by the specialist finder (region/language) and the
   enquiry form (country/language) — one component, not two.

   Auto-enhances on load and watches for selects added later
   (modals) or options populated later (specialist regions are
   filled by JS after render). Idempotent — safe to load twice.
   ============================================================ */
(function () {
  'use strict';
  if (window.__amalgamCustomSelect) return;
  window.__amalgamCustomSelect = true;

  var openWrap = null;

  function closeOpen() {
    if (!openWrap) return;
    openWrap.classList.remove('cs--open', 'cs--up');
    var t = openWrap.querySelector('.cs__trigger');
    var l = openWrap.querySelector('.cs__list');
    if (t) t.setAttribute('aria-expanded', 'false');
    if (l) l.hidden = true;
    openWrap = null;
  }

  document.addEventListener('click', function (e) {
    if (openWrap && !openWrap.contains(e.target)) closeOpen();
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && openWrap) {
      var t = openWrap.querySelector('.cs__trigger');
      closeOpen();
      if (t) t.focus();
    }
  });
  // The list is fixed to the trigger's position — if the page/modal scrolls
  // under it, close (ignore scrolling inside the list itself).
  window.addEventListener('scroll', function (e) {
    if (openWrap && !openWrap.contains(e.target)) closeOpen();
  }, true);
  window.addEventListener('resize', closeOpen);

  function enhance(select) {
    if (!select || select.multiple || select.dataset.csDone === '1') return;
    select.dataset.csDone = '1';

    var wrap = document.createElement('div');
    wrap.className = 'cs';

    var trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'cs__trigger';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');

    var list = document.createElement('ul');
    list.className = 'cs__list';
    list.setAttribute('role', 'listbox');
    list.hidden = true;

    select.parentNode.insertBefore(wrap, select);
    wrap.appendChild(select);
    wrap.appendChild(trigger);
    wrap.appendChild(list);

    // Native select becomes value-holder only: hidden + off the tab order.
    select.classList.add('cs__native');
    select.setAttribute('tabindex', '-1');
    select.setAttribute('aria-hidden', 'true');

    function renderList() {
      list.innerHTML = '';
      Array.prototype.forEach.call(select.options, function (opt) {
        var li = document.createElement('li');
        li.className = 'cs__option';
        li.setAttribute('role', 'option');
        li.dataset.value = opt.value;
        // Optional leading flag/icon per option (opt-in via data-cs-flag on the
        // <option>). Backward-compatible: without it, text-only as before.
        if (opt.dataset.csFlag) {
          li.classList.add('cs__option--flagged');
          var flag = document.createElement('span');
          flag.className = 'cs__flag cs__flag--' + opt.dataset.csFlag;
          flag.setAttribute('aria-hidden', 'true');
          li.appendChild(flag);
          li.appendChild(document.createTextNode(opt.textContent));
        } else {
          li.textContent = opt.textContent;
        }
        if (opt.disabled) li.classList.add('cs__option--disabled');
        if (opt.value === '') li.classList.add('cs__option--placeholder');
        li.addEventListener('click', function () {
          if (opt.disabled) return;
          if (select.value !== opt.value) {
            select.value = opt.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
          syncTrigger();
          closeOpen();
          trigger.focus();
        });
        list.appendChild(li);
      });
      syncSelected();
    }

    function syncTrigger() {
      var opt = select.options[select.selectedIndex];
      // Optional fixed trigger label (opt-in via data-cs-trigger-label) — e.g. a
      // translated "LANGUAGE" that stays put instead of showing the current value.
      if (select.dataset.csTriggerLabel) {
        trigger.textContent = select.dataset.csTriggerLabel;
        trigger.classList.remove('cs__trigger--placeholder');
      } else {
        trigger.textContent = opt ? opt.textContent : '';
        trigger.classList.toggle('cs__trigger--placeholder', !select.value);
      }
      syncSelected();
    }

    function syncSelected() {
      Array.prototype.forEach.call(list.children, function (li) {
        var on = li.dataset.value === select.value && select.value !== '';
        li.setAttribute('aria-selected', on ? 'true' : 'false');
        li.classList.toggle('cs__option--selected', on);
      });
    }

    // List is position:fixed, so place it against the trigger in viewport
    // coords — flips above when there isn't room below.
    function place() {
      var r = trigger.getBoundingClientRect();
      list.style.top = '-9999px';
      // Width: default matches the trigger (form fields). Opt-in data-cs-fit
      // sizes to content with the trigger width as a floor — for the narrow
      // masthead trigger whose options ("LANGUAGE" list + flags) are wider.
      if (select.dataset.csFit === '1') {
        list.style.width = 'auto';
        list.style.minWidth = r.width + 'px';
      } else {
        list.style.width = r.width + 'px';
        list.style.minWidth = '';
      }
      // Horizontal: left-align to the trigger, but never overflow the viewport;
      // data-cs-align="end" (or an overflow) right-aligns to the trigger — needed
      // for the right-edge masthead selector.
      list.style.left = '0';
      var lw = list.offsetWidth;
      var left = r.left;
      if (select.dataset.csAlign === 'end' || left + lw > window.innerWidth - 8) {
        left = Math.max(8, r.right - lw);
      }
      list.style.left = left + 'px';
      // Vertical: flip above when there isn't room below.
      var lh = list.offsetHeight;
      var below = window.innerHeight - r.bottom;
      list.style.top = (below < lh + 8 && r.top > below)
        ? Math.max(8, r.top - lh) + 'px'
        : r.bottom + 'px';
    }

    function open() {
      closeOpen();
      list.hidden = false;
      wrap.classList.add('cs--open');
      trigger.setAttribute('aria-expanded', 'true');
      openWrap = wrap;
      place();
      var sel = list.querySelector('.cs__option--selected');
      if (sel) sel.scrollIntoView({ block: 'nearest' });
    }

    function toggle() { (openWrap === wrap) ? closeOpen() : open(); }

    trigger.addEventListener('click', function (e) { e.preventDefault(); toggle(); });
    trigger.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (openWrap !== wrap) { open(); return; }
        step(e.key === 'ArrowUp' ? -1 : 1);
      }
    });

    function step(dir) {
      var opts = Array.prototype.slice.call(list.children).filter(function (li) {
        return !li.classList.contains('cs__option--disabled') && !li.classList.contains('cs__option--placeholder');
      });
      if (!opts.length) return;
      var cur = opts.findIndex(function (li) { return li.getAttribute('aria-selected') === 'true'; });
      var next = Math.max(0, Math.min(opts.length - 1, cur + dir));
      var li = opts[next];
      if (select.value !== li.dataset.value) {
        select.value = li.dataset.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      syncTrigger();
      li.scrollIntoView({ block: 'nearest' });
    }

    renderList();
    syncTrigger();

    // Native select changed elsewhere → keep trigger in sync.
    select.addEventListener('change', syncTrigger);
    // Options populated/changed later (specialist regions) → rebuild the list.
    new MutationObserver(function () { renderList(); syncTrigger(); })
      .observe(select, { childList: true });
  }

  function scan(root) {
    (root || document).querySelectorAll('select[data-custom-select]').forEach(enhance);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(); });
  } else {
    scan();
  }

  // Selects can arrive with modals rendered later in the page — watch for them.
  var bodyMo = new MutationObserver(function (muts) {
    muts.forEach(function (m) {
      Array.prototype.forEach.call(m.addedNodes, function (n) {
        if (n.nodeType !== 1) return;
        if (n.matches && n.matches('select[data-custom-select]')) enhance(n);
        else if (n.querySelectorAll) scan(n);
      });
    });
  });
  (function start() {
    if (document.body) bodyMo.observe(document.body, { childList: true, subtree: true });
    else setTimeout(start, 50);
  })();

  window.CustomSelect = { enhance: enhance, scan: scan };
})();
